import { Component, computed, input } from '@angular/core';
import { AdminCardComponent, AdminIconComponent } from '../../../shared/admin';
import type { FunnelAnalytics } from '../../../shared/models/funnel-analytics.models';

type FunnelInsightItem = {
  icon: 'arrow-down' | 'arrow-up' | 'bar-chart-3' | 'sparkles';
  title: string;
  description: string;
};

@Component({
  selector: 'app-dashboard-funnel-insights',
  standalone: true,
  imports: [AdminCardComponent, AdminIconComponent],
  templateUrl: './dashboard-funnel-insights.component.html',
  styleUrl: './dashboard-funnel-insights.component.scss',
})
export class DashboardFunnelInsightsComponent {
  readonly leadAnalytics = input<FunnelAnalytics | null>(null);
  readonly applicantAnalytics = input<FunnelAnalytics | null>(null);

  readonly hasData = computed(() => Boolean(this.leadAnalytics() || this.applicantAnalytics()));

  readonly leadInsights = computed(() => this.buildEntityInsights(this.leadAnalytics(), 'lead'));
  readonly applicantInsights = computed(() =>
    this.buildEntityInsights(this.applicantAnalytics(), 'applicant'),
  );

  private buildEntityInsights(
    analytics: FunnelAnalytics | null,
    entity: 'lead' | 'applicant',
  ): FunnelInsightItem[] {
    if (!analytics) {
      return [];
    }

    const items: FunnelInsightItem[] = [];
    const entityLabel = entity === 'lead' ? 'Lead' : 'Applicant';

    if (analytics.largestDropOff) {
      items.push({
        icon: 'arrow-down',
        title: `Largest ${entityLabel} Drop-off`,
        description: `${analytics.largestDropOff.fromStatusName} → ${analytics.largestDropOff.toStatusName} (${analytics.largestDropOff.rate}% drop)`,
      });
    }

    if (analytics.bestConversionStage && analytics.bestConversionStage.rate > 0) {
      items.push({
        icon: 'arrow-up',
        title: `Best ${entityLabel} Conversion Stage`,
        description: `${analytics.bestConversionStage.fromStatusName} → ${analytics.bestConversionStage.toStatusName} (${analytics.bestConversionStage.rate}% conversion)`,
      });
    }

    items.push({
      icon: 'bar-chart-3',
      title: `${entityLabel} Overall Conversion`,
      description: `${analytics.overallConversionRate}% from first stage to ${analytics.convertedStageName || 'completed stage'}`,
    });

    for (const recommendation of analytics.recommendations) {
      items.push({
        icon: 'sparkles',
        title: `${entityLabel} Recommendation`,
        description: recommendation,
      });
    }

    return items;
  }
}
